import { transformUserRoleToIssuerRole } from 'auth';
import classNames from 'classnames';
import { MouseEvent, useCallback, useContext } from 'react';
import IssuerModal from '@/modules/common/components/modals/IssuerModal.tsx';
import { useUpdateUserPermissionsMutation } from '@/modules/common/queries/permissions.ts';
import {
  AdminAddIssuerRegisterModal,
  AdminAddIssuerRegisterModalProps,
} from '@/modules/admin-dashboard/components/modals/AdminAddIssuerRegisterModal.tsx';
import { useAdminDeleteUserRegisterMutation } from '@/modules/admin-dashboard/queries/issuers.tsx';
import { Avatar, Button, ConfirmModal, DisclosureListItem, ModalProviderContext } from 'ui';
import { EditIcon, EmptyFolderIcon, TrashGrayIcon } from 'ui/icons';
import { IssuerResponse, RegisterParty } from 'api';

interface AdminIssuerItemProps {
  issuer: IssuerResponse;
}

export const AdminIssuerItem = ({ issuer }: AdminIssuerItemProps) => {
  const { openModal, onCloseAll } = useContext(ModalProviderContext);
  const { id, first_name, last_name, registers, avatar_url, email, user_registers } = issuer;

  const { mutateAsync: deleteUserRegister } = useAdminDeleteUserRegisterMutation({
    onSuccess: onCloseAll,
  });

  const handleOpenAddIssuerRegisterModal = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      openModal<AdminAddIssuerRegisterModalProps>(AdminAddIssuerRegisterModal, {
        issuer_id: id,
        issuer_register_ids: (registers || []).map(({ id }) => id),
      });
    },
    [id, openModal, registers],
  );

  const handleOpenRedactIssuerModal = useCallback(
    (register: RegisterParty) => {
      const userRole = (issuer?.user_registers || []).find(
        r => r.register_id === register.id,
      )?.user_role;
      const issuerRole = userRole ? transformUserRoleToIssuerRole(userRole) : null;
      openModal(IssuerModal, {
        issuer: issuerRole
          ? { ...issuer, issuer_role: issuerRole, user_registers: issuer.user_registers || [] }
          : { ...issuer, user_registers: issuer.user_registers || [] },
        register,
        isUpdating: true,
        mutation: [useUpdateUserPermissionsMutation, {}],
      });
    },
    [issuer, openModal],
  );

  const handleOpenDeleteIssuerModal = useCallback(
    (user_register_id: number, register_name: string) => {
      if (!user_register_id) return;
      openModal(ConfirmModal, {
        onConfirm: async () => {
          await deleteUserRegister(user_register_id);
        },
        closeAfterConfirm: false,
        title: (
          <p>
            You sure you want to remove <span className="text-blue-700">{register_name}</span> from
            issuer?
          </p>
        ),
      });
    },
    [deleteUserRegister, openModal],
  );

  return (
    <DisclosureListItem
      leftContent={<Avatar src={avatar_url} firstName={first_name} lastName={last_name} />}
      leftMainString={first_name + ' ' + last_name}
      leftSubduedContentSet={[email, 'Registers: ' + registers?.length]}
      rightContent={
        <Button variant="plain" onClick={handleOpenAddIssuerRegisterModal}>
          Add register
        </Button>
      }
      bottomCustomContent={
        registers && Boolean(registers.length) ? (
          <div className="w-full flex flex-col bg-white border-t border-gray-200">
            {registers.map((register, index) => {
              const { name, id: register_id } = register;
              return (
                <div
                  key={'registers_' + register_id}
                  className={classNames(
                    'w-full flex items-center justify-between px-2 sm:px-6 py-[14px]',
                    {
                      'border-b border-gray-200': index !== registers.length - 1,
                    },
                  )}
                >
                  <div className="flex items-center space-x-3">
                    <Avatar firstName={name} lastName={String(register_id)} />
                    <p className="text-[14px] leading-[22px] font-medium">{name}</p>
                  </div>
                  <div className="space-x-3 select-none">
                    <button onClick={handleOpenRedactIssuerModal.bind(null, register)}>
                      <img src={EditIcon} alt="Edit register permissons" />
                    </button>
                    <button
                      onClick={() =>
                        handleOpenDeleteIssuerModal(
                          user_registers?.find(({ register_id: id }) => register_id === id)?.id ||
                            0,
                          name,
                        )
                      }
                    >
                      <img src={TrashGrayIcon} alt="Remove register" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col justify-center items-center gap-6 h-[200px] border-t">
            <div className="flex items-center justify-center w-[36px] h-[36px] bg-[#E3E3E3] rounded-full">
              <img alt="no results" src={EmptyFolderIcon} className="w-4 h-4" />
            </div>
            <div className="text-xl text-neutral-900">No registers yet</div>
          </div>
        )
      }
    />
  );
};
